import { useState, useEffect, Dispatch, SetStateAction } from "react";
import Files from "react-butterfiles";
import UploadItem from "./UploadItem";
import { SelectedFile, ProcessingFile, SelectedFileError } from "./types";
import { notifyBuilder } from "./utils";
import { UploadArea } from "../uploadArea";


type FilesSummary = {
    error: number;
    success: number;
    pending: number;
};


type UploaderProps = {
    allowedMimeTypes?: string[];
    label?: string;
    multiple?: boolean;
    onComplete: (files: ProcessingFile[]) => Promise<any>;
    notifyEvents?: (data: Record<string, any>) => Promise<any>;
    tryAgain: (key:string) => void
    abortUpload: (key:string) => void
    fileToUpload: Record<string, ProcessingFile>
    setFileToUpload: Dispatch<SetStateAction<Record<string, ProcessingFile>>>
};

const Uploader = ({
    label = "",
    tryAgain,
    abortUpload,
    onComplete,
    notifyEvents = null,
    allowedMimeTypes = null,
    multiple = false,
    fileToUpload,
    setFileToUpload
}: UploaderProps) => {
    const [filesWithSuccess, setFilesWithSuccess] = useState(0);
    const [filesWithError, setFilesWithError] = useState(0);
    const [filesTotal, setFilesTotal] = useState(0);

    const notify = notifyBuilder(notifyEvents);


    useEffect(() => {
        const initialData = { pending: 0, success: 0, error: 0 };
        const summary = Object.values(fileToUpload).reduce((acc: FilesSummary, file: Record<string, any>) => {
            if (["success", "uploaded"].includes(file.status)) {
                acc.success += 1;
                return acc;
            }

            if (file.status === "error") {
                acc.error += 1;
                return acc;
            }

            acc.pending += 1;
            return acc;
        }, initialData) as FilesSummary;

        setFilesWithError(summary.error);
        setFilesWithSuccess(summary.success);
        setFilesTotal(summary.error + summary.success + summary.pending);
    }, [fileToUpload]);

    useEffect(() => {
        const processed = filesWithError + filesWithSuccess;
        if (processed >= filesTotal && filesTotal > 0) {
            const uploadedFiles = Object.values(fileToUpload).filter(item => item.status === "uploaded");

            if (!uploadedFiles.length) {
                return;
            }

            onComplete(uploadedFiles)
                .then((result: any) => {
                    setFileToUpload(data => {
                        Object.keys(result).forEach(filename => {
                            data[filename].status = result[filename].status;
                        });

                        return { ...data };
                    });
                })
                .catch(e => {
                    console.error(e);
                    setFileToUpload(data => {
                        Object.keys(data).forEach(filename => {
                            if (data[filename].status === "uploaded") {
                                data[filename].status = "error";
                                data[filename].message = "Não foi possível registrar o arquivo";
                            }
                        });

                        return { ...data };
                    });
                });
        }
    }, [filesWithSuccess, filesWithError, filesTotal, fileToUpload, onComplete]);

    return (
        <Files
            accept={allowedMimeTypes || []}
            multiple={multiple}
            multipleMaxSize={1000000000}
            maxSize={1000000000}
            onSuccess={async (files: SelectedFile[]) => {
                const uploadedFiles = files.map(file => ({
                    ...file,
                    name: file.name.replace(/[^a-zA-Z0-9()|,\-. ]/g, "_").replace("__s__", "_|s|_")
                }));

                uploadedFiles.forEach(uploadedFile => {
                    const file: ProcessingFile = {
                        file: uploadedFile,
                        status: "pending",
                        uploaded: 0,
                        uploadPercent: 0
                    };
                    setFileToUpload((data: any) => ({ ...data, [uploadedFile.name]: file }));
                });
            }}
            onError={(errors: SelectedFileError[]) => {
                notify({ type: "error", action: "acceptFailed", detail: { errors } });
                errors.forEach(error => {
                    let message = "Não foi possível adicionar o arquivo para upload";
                    const description = "";

                    if (error.type === "multipleMaxSizeExceeded") {
                        message = "Arquivo muito grande";
                    }

                    if (error.type === "unsupportedFileType") {
                        message = `Arquivo do tipo ${error.file.type} não suportado`;
                    }

                    alert(`${message}. ${description}`);
                });
            }}
        >
            {({ browseFiles, getDropZoneProps, getLabelProps }) => (
                <>
                    {label ? <label {...getLabelProps()}>{label}</label> : ""}

                    <div {...getDropZoneProps({ className: "file-dropzone" })}>
                        <UploadArea browseFiles={browseFiles}>
                            <div className="row" style={{ margin: "0 0.5rem", paddingBottom: "2rem" }}>
                                {Object.values(fileToUpload).map((toUpload, index) => (
                                    <div className="col-md-3" key={toUpload?.file?.name ?? index}>
                                        <UploadItem
                                            fileDescriptor={toUpload}
                                            onAbort={abortUpload}
                                            onRetry={tryAgain}
                                        />
                                    </div>
                                ))}
                            </div>
                        </UploadArea>
                    </div>
                </>
            )}
        </Files>
    );
};

export default Uploader;
